import {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
    fetchBaseQuery,
} from "@reduxjs/toolkit/query";
import type { AuthRootState } from "@features/auth/types";
import { BASE_URL } from "../constants";
import { authTokenChange, logoutUser } from "@features/auth/slices";
import { handleGlobalError } from "@features/error/helpers";

const baseQuerySimple: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = fetchBaseQuery({
    baseUrl: `${BASE_URL}/`,
    credentials: "include",
    prepareHeaders: (headers, { getState }) => {
        const { token } = (getState() as AuthRootState).auth;
        if (token) headers.set("Authorization", `Bearer ${token}`);
        return headers;
    },
});

interface RefreshResponse {
    accessToken: string;
}

// Lock for refresh requests this is essential
let refreshLock: Promise<string | null> | null = null;

export const baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    const { isAuthInitialized, token } = (api.getState() as AuthRootState).auth;

    // Making initial request
    const result = await baseQuerySimple(args, api, extraOptions);
    const error = result.error as FetchBaseQueryError | undefined;
    // User never logged in → so we logout 
    if (!isAuthInitialized) {
        api.dispatch(logoutUser());
        return result;
    }

    // If token exists and no error → return result
    if (token && !error) {
        return result;
    }

    // Check if we need to refresh (401 unauthorized) we will receive 401 gurranted here 
    const needRefreshing = error?.status === 401;

    if (needRefreshing) {
        // If a refresh is already in progress, we will wait for it.
        if (!refreshLock) {
            const refreshPromise = (async () => {
                const refreshResult = await baseQuerySimple(
                    {
                        url: "/admin/auth/refresh-token",
                        method: "POST",
                        credentials: "include",
                    },
                    api,
                    extraOptions
                );
                const refreshError = refreshResult.error as FetchBaseQueryError | undefined;

                if (refreshError?.status === 401) {
                    api.dispatch(logoutUser());
                    refreshLock = null;
                    return null;
                }
                // If other error or no data, we are returning null without logout
                if (refreshResult.error || !refreshResult.data) {
                    refreshLock = null;
                    return null;
                }

                const { accessToken } = refreshResult.data as RefreshResponse;
                api.dispatch(authTokenChange({ accessToken }));
                refreshLock = null;
                return accessToken;
            })();
            refreshLock = refreshPromise
        }

        // Retry original request with new token
        //Capture the reference NOW, before finally can null the module-level variable
        const pendingRefresh = refreshLock;
        const newToken = await pendingRefresh;

        if (newToken) {
            const retryResult = await baseQuerySimple(args, api, extraOptions);
            handleGlobalError(retryResult.error, api);
            return retryResult;
        }

    }

    handleGlobalError(result.error, api);


    return result;
};
